import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { filterSuggestionItems } from '@blocknote/core';
import { SuggestionMenuController, useCreateBlockNote, useEditorChange } from '@blocknote/react';
import { BlockNoteView } from '@blocknote/shadcn';

import { useBrainVariables } from '@/hooks/useBrainVariables';
import { cn } from '@/lib/utils';

import {
  buildContextVariableLookup,
  createMediaStrippingPasteHandler,
  getContextVariableMenuItems,
  normalizeBlocksWithContextVariables,
  parseMarkdownToBlocks,
  schema,
} from '../Editors/BlockNoteEditor/editorUtils';

import '@blocknote/shadcn/style.css';
import '../BlockNoteEditor/BlockNoteEditor.scss';

export interface GuidelineEditorProps {
  value: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  placeholder?: string;
  label?: string;
  errorMessage?: string;
  disabled?: boolean;
}

export const GuidelineEditor = ({
  value,
  onChange,
  onFocus,
  placeholder,
  label,
  errorMessage,
  disabled = false,
}: GuidelineEditorProps) => {
  const { contextVariables } = useBrainVariables();
  const [isInitialized, setIsInitialized] = useState(false);
  const lastInitializedValueRef = useRef<string | null>(null);

  // Creates a new editor instance configured with our schema
  const editor = useCreateBlockNote({
    schema,
    pasteHandler: createMediaStrippingPasteHandler(),
    placeholders: { default: placeholder },
  });

  // Ref to track if we're programmatically updating to avoid feedback loops
  const isUpdatingRef = useRef(false);

  // Build a lookup map keyed by context variable id for quick normalization
  const contextVariableLookup = useMemo(
    () => buildContextVariableLookup(contextVariables),
    [contextVariables]
  );

  // Derive mention suggestions from the active context variables
  const mentionMenuItems = useMemo(
    () => getContextVariableMenuItems(editor, contextVariables),
    [contextVariables, editor]
  );

  // Function to load and normalize content
  const loadAndNormalizeContent = useCallback(
    async (contentToLoad: string) => {
      const blocks = await parseMarkdownToBlocks(editor, contentToLoad || '');

      // Normalize context-variable placeholders in the parsed blocks
      const { normalized, changed } = normalizeBlocksWithContextVariables(
        blocks,
        contextVariableLookup
      );

      // Replace the document with the normalized blocks
      const blocksToApply = changed ? normalized : blocks;
      editor.replaceBlocks(editor.document, blocksToApply);
    },
    [editor, contextVariableLookup]
  );

  // Initialize editor state and re-initialize when value changes externally
  useEffect(() => {
    // Re-initialize if:
    // 1. Never initialized before
    // 2. Value changed externally (different from what we last initialized with)
    // This handles switching between different conditions
    const valueChanged = lastInitializedValueRef.current !== value;
    const shouldInitialize = !isInitialized || valueChanged;

    if (!shouldInitialize) {
      return;
    }

    isUpdatingRef.current = true;

    (async () => {
      await loadAndNormalizeContent(value);
      lastInitializedValueRef.current = value;
      setIsInitialized(true);
      isUpdatingRef.current = false;
    })();
  }, [value, isInitialized, loadAndNormalizeContent]);

  // Re-sync context variable placeholders whenever the lookup changes
  useEffect(() => {
    if (!isInitialized || contextVariableLookup.size === 0) {
      return;
    }

    const currentBlocks = editor.document;
    const { normalized, changed } = normalizeBlocksWithContextVariables(
      currentBlocks,
      contextVariableLookup
    );

    if (!changed) {
      return;
    }

    isUpdatingRef.current = true;
    editor.replaceBlocks(editor.document, normalized);
    isUpdatingRef.current = false;
  }, [contextVariableLookup, editor, isInitialized]);

  // Track content changes and notify parent
  useEditorChange(
    useCallback(async () => {
      if (!isInitialized || isUpdatingRef.current) {
        return;
      }

      // Convert blocks to plain text (markdown without formatting)
      const markdown = await editor.blocksToMarkdownLossy(editor.document);
      // Strip any trailing newlines that BlockNote might add
      const cleanedValue = markdown.trim();

      // Update the ref to prevent re-initialization when the value prop updates
      // due to this change (avoiding infinite loops)
      lastInitializedValueRef.current = cleanedValue;
      onChange(cleanedValue);
    }, [editor, onChange, isInitialized]),
    editor
  );

  return (
    <div className="w-full">
      {label && (
        <label className="mb-1 block text-sm leading-5 font-normal text-foreground">{label}</label>
      )}
      <div
        className={cn(
          'blocknote-editor min-h-20 bg-background',
          errorMessage && 'border-destructive',
          disabled && 'cursor-not-allowed opacity-50'
        )}
        onFocus={onFocus}
      >
        <BlockNoteView
          theme="light"
          editor={editor}
          formattingToolbar={false}
          sideMenu={false}
          slashMenu={false}
          data-theming-css-blocknote
          editable={!disabled}
        >
          <SuggestionMenuController
            triggerCharacter="$"
            getItems={async (query) => filterSuggestionItems(mentionMenuItems, query)}
          />
        </BlockNoteView>
      </div>
      {errorMessage && (
        <span className="mt-1 text-sm leading-5 font-normal text-destructive">{errorMessage}</span>
      )}
    </div>
  );
};

export default GuidelineEditor;
